// src/pages/outsourcing_management/requisitions/RequisitionPartPreview.tsx
//
// EE-234 - the "visual model" for a part in the Requisition part picker is the
// model preview render, the same image the order/quote/basket rows show via
// PreviewButton. PreviewButton isn't reused here because it is a button that
// opens the 3D modal, and the picker needs a plain thumbnail inside an
// Autocomplete option (nesting a button in an option row breaks selection).
//
// The preview endpoint is token-authenticated and returns a blob, so the URL
// is per-session; the module-level cache keeps one request (and one object
// URL) per model no matter how often the dropdown is reopened.
import { FunctionComponent, useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import { apiGetPreviewImage } from "util/network/Products";

const previewCache = new Map<number, Promise<string>>();

const loadPreview = (token: string, modelId: number): Promise<string> => {
  const cached = previewCache.get(modelId);
  if (cached) return cached;
  const request = apiGetPreviewImage(token, modelId);
  // A failed lookup shouldn't be remembered - the model may just not have a
  // render yet when the part was created moments ago.
  request.catch(() => previewCache.delete(modelId));
  previewCache.set(modelId, request);
  return request;
};

type PropsType = {
  token: string;
  modelId?: number;
  /** Rendered box is square; 40 in the dropdown, larger for the selection. */
  size: number;
};

const RequisitionPartPreview: FunctionComponent<PropsType> = ({
  token,
  modelId,
  size,
}) => {
  const [preview, setPreview] = useState<string>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPreview(undefined);
    if (!modelId) return;

    let cancelled = false;
    setLoading(true);
    loadPreview(token, modelId)
      .then((url) => {
        if (!cancelled) setPreview(url);
      })
      .catch(() => {
        // No render for this model - the placeholder below is the answer.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, modelId]);

  return (
    <Box
      sx={{
        height: size,
        width: size,
        // The frame is exactly `size` square - never a flex item that grows,
        // which would leave the image floating in a stretched box.
        flex: "0 0 auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        overflow: "hidden",
      }}
    >
      {loading ? (
        <CircularProgress size={Math.min(20, size / 2)} />
      ) : preview ? (
        <Box
          component="img"
          src={preview}
          alt="Part preview"
          sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
        />
      ) : (
        <ImageNotSupportedOutlinedIcon
          fontSize="small"
          sx={{ color: "text.disabled" }}
        />
      )}
    </Box>
  );
};

export default RequisitionPartPreview;
